import React from "react";
import { Link } from "react-router-dom";
import { Bell, Coffee, Lock, RefreshCw, HelpCircle, Check, X } from "lucide-react";
import Navbar from "../components/Navbar";
import TrackRecordStrip from "../components/TrackRecordStrip";
import PortfolioTeaser from "../components/PortfolioTeaser";
import MoonbucksLogo from "../components/MoonbucksLogo";
import DigestSignup from "../components/DigestSignup";
import { HowItWorksFunnel, HeroSteps } from "../components/HowItWorksFunnel";
import { PlanCta } from "../components/PlanCta";
import { Hero, CoffeeMath, Pillars, BgSection, Headline, Reveal } from "../components/home/HomeParts";
import useSeo from "../hooks/useSeo";

const WHO = "Moonbucks is run by a small, independent research desk. Our scanner does the heavy lifting, and a person on the desk reviews every alert before it goes out. You don’t have to take our word for any of it — every alert we’ve ever sent, win or loss, is public on the track record.";
const TRY = "Yes — start with the Weekly plan: $4.99 for a week of full access. Cancel anytime.";

export default function Landing() {
  useSeo({
    title: "Moonbucks · Analyst-vetted stock alerts for busy investors",
    description: "Analyst-vetted U.S. stock alerts. Two strategies, one subscription, from $4.99/week. Every trade auditable on the public track record.",
    canonical: "https://moonbucks.io/",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": "Moonbucks stock alerts",
        "description": "Analyst-vetted U.S. stock alerts. Two strategies (Steady + Moonshot), one subscription.",
        "brand": { "@type": "Brand", "name": "Moonbucks" },
        "offers": [
          { "@type": "Offer", "name": "Weekly", "price": "4.99", "priceCurrency": "USD", "url": "https://moonbucks.io/#pricing" },
          { "@type": "Offer", "name": "Monthly", "price": "14.99", "priceCurrency": "USD", "url": "https://moonbucks.io/#pricing" }
        ]
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          { "@type": "Question", "name": "Is the win rate real?", "acceptedAnswer": { "@type": "Answer", "text": "Yes — every alert is public at /track-record with entry, exit and reason. Wins and losses. Nothing scrubbed." } },
          { "@type": "Question", "name": "Who is behind Moonbucks?", "acceptedAnswer": { "@type": "Answer", "text": WHO } },
          { "@type": "Question", "name": "Can I try before I buy?", "acceptedAnswer": { "@type": "Answer", "text": TRY } },
          { "@type": "Question", "name": "Is this a Discord pump group?", "acceptedAnswer": { "@type": "Answer", "text": "No. Every alert is analyst-reviewed before it publishes. We do not run coordinated buys or shill for compensation." } },
          { "@type": "Question", "name": "How much should I risk per alert?", "acceptedAnswer": { "@type": "Answer", "text": "Our disclosed portfolio simulation uses 25% of equity per position, 4 slots max. Your position sizing is your decision — this is educational, not investment advice." } },
          { "@type": "Question", "name": "Why is this educational, not investment advice?", "acceptedAnswer": { "@type": "Answer", "text": "Moonbucks does not know your risk tolerance, tax situation or account size. Every subscriber makes their own trade decisions." } }
        ]
      }
    ],
  });

  return (
    <div className="bg-[#07040f] min-h-screen text-white">
      <Navbar />
      <Hero />
      <CoffeeMath />
      <RecordSection />
      <BgSection img="/media/sim.webp" testId="home-simulation">
        <Reveal>
          <Headline>Every alert, followed. Here’s the result.</Headline>
          <p className="mt-5 text-lg text-neutral-300 max-w-xl mx-auto">A $10,000 portfolio that took every alert, measured against the S&amp;P 500.</p>
          <div className="mt-12"><PortfolioTeaser /></div>
        </Reveal>
      </BgSection>
      <HowSection />
      <Pillars />
      <PricingSection />
      <FaqSection />
      <section className="border-t border-white/10" data-testid="home-pulse-band">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16 sm:py-20 flex flex-col items-center text-center">
          <h2 className="font-serif-hero text-4xl sm:text-5xl text-white leading-tight">Not ready yet? Get the free weekly pulse.</h2>
          <DigestSignup />
        </div>
      </section>
      <Footer />
    </div>
  );
}

function RecordSection() {
  return (
    <section className="relative overflow-hidden" data-testid="home-record">
      <img src="/media/record.webp" alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#07040f] via-black/75 to-[#07040f]" />
      <div className="relative py-14 sm:py-20"><Reveal><TrackRecordStrip /></Reveal></div>
    </section>
  );
}

function HowSection() {
  return (
    <section id="how" className="max-w-6xl mx-auto px-6 lg:px-12 py-24 sm:py-32 text-center" data-testid="home-how">
      <Reveal>
        <Headline>How Moonbucks works</Headline>
        <p className="mt-6 text-lg text-neutral-300 leading-relaxed" data-testid="hero-subheader">
          We filter the noise. Moonbucks boils down thousands of data points — prices, headlines, social posts, even the strength of the words people use — into one read: is the market bold, or scared? Then it follows the smart money down to the sector level and hands you today’s move: buy, take profits, play defense, or wait. <span className="text-white">Built for people with a day job. Two minutes, plain English.</span>
        </p>
        <HeroSteps />
      </Reveal>
      <Reveal><HowItWorksFunnel /></Reveal>
      <div className="mt-6 flex flex-col sm:flex-row justify-center gap-x-6 gap-y-2 text-sm">
        <Link to="/track-record" className="text-violet-300 hover:underline underline-offset-4" data-testid="desk-see-trades-link">See every live trade →</Link>
        <Link to="/track-record#how-signals-work" className="text-neutral-400 hover:text-violet-300" data-testid="desk-signals-link">What each signal measures →</Link>
      </div>
      <Reveal className="mt-24 flex flex-col items-center gap-10">
        <div className="max-w-2xl">
          <h3 className="font-serif-hero text-4xl sm:text-5xl text-white leading-tight">Here’s what lands on your phone.</h3>
          <p className="mt-4 text-neutral-400">A paragraph, not a chart. If you can read a text message, you can read a Moonbucks alert.</p>
        </div>
        <div className="w-full max-w-2xl text-left"><SampleAlert /></div>
      </Reveal>
    </section>
  );
}

function SampleAlert() {
  return (
    <figure className="bg-[#120b22] border border-violet-400/30 rounded-2xl overflow-hidden shadow-2xl shadow-violet-900/30" data-testid="sample-alert-card">
      <figcaption className="sr-only">Sample Moonbucks alert for ASTS: a Tier-A Steady setup with a first target, a big target and a safety line.</figcaption>
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
        <span className="flex items-center gap-2 text-violet-300 text-sm"><Bell className="w-4 h-4" /> New alert · 9:47 a.m. ET</span>
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-violet-500/20 border border-violet-400/40 text-violet-200 text-xs font-semibold"><Coffee className="w-3 h-3" /> Steady</span>
      </div>
      <div className="px-5 py-5">
        <div className="flex items-baseline gap-3">
          <div className="font-serif-hero text-4xl text-white">ASTS</div>
          <div className="text-sm text-neutral-400">AST SpaceMobile · $34.18</div>
        </div>
        <p className="mt-3 text-[15px] text-neutral-200 leading-relaxed">
          ASTS has been building quietly for three weeks — more buyers than sellers while the price barely moved. Today it broke out of that range on more than twice its normal trading volume, and the big money looks to be adding, not selling. It’s rising without looking overheated. Our desk rates this a <span className="font-semibold text-violet-300">Tier-A Steady setup</span>. Buy zone: $33.90–$34.30.
        </p>
        <div className="mt-5 grid grid-cols-3 gap-3 text-center">
          {[["First target", "+2.5%", "text-emerald-400 border-emerald-500/30"], ["Big target", "+12%", "text-emerald-400 border-emerald-500/30"], ["Safety line", "−7%", "text-red-400 border-red-500/30"]].map(([l, v, c]) => (
            <div key={l} className={`rounded-lg border bg-white/[0.03] p-2.5 ${c}`}>
              <div className="text-xs text-neutral-400">{l}</div>
              <div className="font-serif-hero text-2xl">{v}</div>
            </div>
          ))}
        </div>
        <div className="mt-3 text-[11px] text-neutral-500 italic">Example alert — for illustration only. Not investment advice.</div>
      </div>
    </figure>
  );
}

const COMPARE_ROWS = [
  ["Every alert reviewed by a human before publish", true, false],
  ["Live, ongoing track record (not a back-test)", true, false],
  ["Public per-trade audit — every win AND loss", true, false],
  ["$10K portfolio simulation you can inspect", true, false],
  ["Plain-English alert (paragraph, not chart)", true, false],
  ["Analyst-vetted BUY shortlist", true, false],
  ["Push + email + Discord delivery", true, false],
  ["Cancel anytime", true, true],
];

function PriceCard({ plan, price, per, sub, features, highlight, testId, ctaClass }) {
  return (
    <div className={`rounded-2xl p-8 ${highlight ? "border-2 border-violet-400 bg-violet-500/10" : "border border-white/10 bg-white/[0.03]"}`} data-testid={testId}>
      <div className="font-serif-hero text-3xl text-white">{plan}</div>
      {highlight && <div className="mt-1 text-sm text-violet-300">Most people pick Monthly.</div>}
      <div className="mt-5 flex items-baseline gap-2">
        <span className="font-serif-hero text-6xl text-white">{price}</span>
        <span className="text-neutral-400">/{per}</span>
      </div>
      <p className="mt-2 text-sm text-neutral-400">{sub}</p>
      <ul className="mt-6 space-y-3 text-sm text-neutral-200">
        {features.map((f) => <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-violet-300 mt-0.5" />{f}</li>)}
      </ul>
      <PlanCta planId={plan.toLowerCase()} className={ctaClass} testId={`pricing-${plan.toLowerCase()}-cta`} />
    </div>
  );
}

function PricingSection() {
  return (
    <section id="pricing" className="border-t border-white/10" data-testid="home-pricing">
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-24 sm:py-32 text-center">
        <Reveal className="max-w-3xl mx-auto">
          <Headline>Less than a cup of coffee.</Headline>
          <p className="mt-6 text-lg text-neutral-300" data-testid="pricing-policy-line">No lock-in. Cancel anytime in two clicks — your access runs to the end of the period you’ve paid for. Alerts are delivered live, so paid periods aren’t refunded.</p>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto text-left">
          <PriceCard plan="Weekly" price="$4.99" per="week" sub="~$0.71/day · cheaper than a black coffee" testId="pricing-card-weekly" ctaClass="bg-white/10 hover:bg-white/20 text-white h-12"
            features={["Live breakout screener", "Analyst-vetted BUY alerts", "Full audit — every trade, every outcome", "Push + email + Discord delivery", "Cancel anytime"]} />
          <PriceCard plan="Monthly" price="$14.99" per="month" sub="Save ~25% · skip 3 lattes" highlight testId="pricing-card-monthly" ctaClass="bg-mb-emerald hover:bg-mb-emerald-dark text-white h-12"
            features={["Everything in Weekly", "Save ~25% vs paying weekly", "Discord subscriber channel", "Custom portfolio scenario picker", "Cancel anytime"]} />
        </div>
        <div className="mt-6 max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-left" data-testid="pricing-trust-strip">
          <div className="flex items-start gap-3">
            <RefreshCw className="w-5 h-5 text-violet-300 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-sm text-white">Cancel anytime in two clicks.</div>
              <div className="text-xs text-neutral-400">Your access runs to the end of the period you’ve paid for.</div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-neutral-400"><Lock className="w-4 h-4" /> Secure checkout via Stripe · Apple Pay accepted</div>
        </div>
        <div className="mt-6 max-w-4xl mx-auto overflow-x-auto rounded-2xl border border-white/10" data-testid="pricing-vs-competitors">
          <table className="w-full text-sm min-w-[480px]">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left px-5 py-4 font-medium text-neutral-400">Feature</th>
                <th className="text-center px-4 py-4 bg-violet-500/10"><div className="font-serif-hero text-xl text-violet-200">Moonbucks</div><div className="text-xs text-violet-300/70 mt-0.5">$14.99/mo</div></th>
                <th className="text-center px-4 py-4"><div className="font-serif-hero text-xl text-neutral-300">Others</div><div className="text-xs text-neutral-500 mt-0.5">$19–47/mo</div></th>
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map(([label, mb, others]) => (
                <tr key={label} className="border-t border-white/5">
                  <td className="px-5 py-3 text-neutral-200">{label}</td>
                  <td className="px-4 py-3 text-center bg-violet-500/10">{mb ? <Check className="w-5 h-5 text-violet-300 mx-auto" strokeWidth={3} /> : <X className="w-5 h-5 text-neutral-600 mx-auto" />}</td>
                  <td className="px-4 py-3 text-center">{others ? <Check className="w-5 h-5 text-violet-300 mx-auto" strokeWidth={3} /> : <X className="w-5 h-5 text-neutral-600 mx-auto" />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-[11px] text-neutral-500 max-w-4xl mx-auto">Comparison based on the four most-searched stock-alert services (Finviz Elite, Mindful Trader, SwingTradeBot, Stock Alarm). Moonbucks is educational — not investment advice.</p>
        <p className="mt-6 text-xs text-neutral-500">Disclaimer: Moonbucks is for educational purposes. Not investment advice. Past performance is not predictive of future results.</p>
      </div>
    </section>
  );
}

const FAQS = [
  { q: "Is the win rate real?", a: (<>Yes. Every alert is public at <Link to="/track-record" className="text-violet-300 underline">/track-record</Link> with entry, exit and reason. Wins and losses. Nothing scrubbed. The <Link to="/portfolio" className="text-violet-300 underline">$10,000 portfolio simulation</Link> shows what a subscriber who followed every alert would have today.</>) },
  { q: "Who is behind Moonbucks?", a: WHO },
  { q: "Can I try before I buy?", a: TRY },
  { q: "Is this a Discord pump group?", a: "No. Every alert is analyst-reviewed before it publishes. We do not run coordinated buys, and we are never compensated by the companies whose tickers we cover." },
  { q: "How much should I risk per alert?", a: "Our disclosed portfolio simulation uses 25% of equity per position with a 4-position max. Your actual position sizing is your decision — Moonbucks is educational, not personal investment advice." },
  { q: "Why is this educational, not investment advice?", a: "Investment advice requires understanding your specific risk tolerance, tax situation and account size. We don't know those. Every subscriber makes their own trade decisions with our signal as one input among many." },
];

function FaqSection() {
  return (
    <section id="faq" className="border-t border-white/10" data-testid="home-faq">
      <div className="max-w-4xl mx-auto px-6 lg:px-12 py-24 sm:py-32 text-center">
        <Reveal><Headline>Frequently asked. Directly answered.</Headline></Reveal>
        <div className="mt-12 divide-y divide-white/10 border-y border-white/10" data-testid="faq-list">
          {FAQS.map((f, i) => (
            <details key={f.q} className="group" data-testid={`faq-item-${i}`}>
              <summary className="cursor-pointer list-none flex items-center justify-center gap-3 py-6">
                <span className="font-serif-hero text-2xl sm:text-3xl text-white">{f.q}</span>
                <HelpCircle className="w-5 h-5 text-violet-300 flex-shrink-0 group-open:rotate-180 transition-transform" />
              </summary>
              <div className="pb-6 -mt-1 text-base text-neutral-300 leading-relaxed max-w-3xl mx-auto">{f.a}</div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-white/10 py-10 pb-16">
      <div className="max-w-6xl mx-auto px-6 lg:px-12 flex flex-col items-center text-center gap-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-mb-emerald flex items-center justify-center text-white"><MoonbucksLogo className="w-4 h-4" /></div>
          <span className="font-serif-hero text-xl">Moonbucks</span>
        </div>
        <div className="flex items-center gap-5 text-xs text-neutral-500 flex-wrap justify-center">
          <Link to="/features" className="hover:text-violet-300" data-testid="footer-features-link">Features</Link>
          <Link to="/pricing" className="hover:text-violet-300" data-testid="footer-pricing-link">Pricing</Link>
          <Link to="/track-record" className="hover:text-violet-300" data-testid="footer-tr-link">Track record</Link>
          <Link to="/vs/stock-alarm" className="hover:text-violet-300" data-testid="footer-vs-stockalarm">vs Stock Alarm</Link>
          <Link to="/vs/mindful-trader" className="hover:text-violet-300" data-testid="footer-vs-mindful">vs Mindful Trader</Link>
          <Link to="/vs/finviz" className="hover:text-violet-300" data-testid="footer-vs-finviz">vs Finviz</Link>
          <Link to="/terms" className="hover:text-violet-300" data-testid="footer-terms-link">Terms</Link>
          <Link to="/privacy" className="hover:text-violet-300" data-testid="footer-privacy-link">Privacy</Link>
          <span data-testid="footer-support-text">Questions? Email <a href="mailto:hello@moonbucks.io" className="text-violet-300 hover:underline" data-testid="footer-support-link">hello@moonbucks.io</a> — a real person reads every message.</span>
          <span>© 2026 Moonbucks. Skip the latte.</span>
        </div>
      </div>
    </footer>
  );
}
